import { PropsWithChildren, FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { MailOutlined } from '@ant-design/icons'

import Box from '~/core/Box'
import Button from '~/core/Button'
import { getUserIdentity } from '~/redux-modules/identity'
import { useGetUserBusinessAccountStatusQuery } from '~/gql/graphql'
import { getModal } from '~/redux-modules/modals'

import RequestBusinessAccountInviteModal from './RequestBusinessAccountInviteModal'
import FailedInvitesModal from './RequestBusinessAccountInviteModal/FailedInvitesModal'
import { BUSINESS_ACCOUNT_INVITE_MODAL } from './constants'

const PLACEMENT = 'my-account-team-members'

interface BusinessAccountInviteModalType {
    showAsLink?: boolean
}

export const BusinessAccountInviteModal: FC<
    PropsWithChildren<BusinessAccountInviteModalType>
> = ({ showAsLink = false }) => {
    const [modalVisible, setModalVisible] = useState<boolean>(false)
    const [failedEmails, setFailedEmails] = useState<string[]>([]) // shows error modal if non-empty array
    const { id } = useSelector(getUserIdentity)

    const { isOpen } = useSelector(state => {
        return getModal(state, BUSINESS_ACCOUNT_INVITE_MODAL)
    })

    useEffect(() => {
        if (isOpen) {
            setModalVisible(true)
        }
    }, [isOpen])

    const businessAccountStatusQuery = useGetUserBusinessAccountStatusQuery({
        variables: { userId: id },
    })

    const data = businessAccountStatusQuery.data?.getUserBusinessAccountStatus

    if (!data) {
        return null
    }

    const { businessAccountId, businessAccountName, isBusinessAccountMember } =
        data

    if (
        !isBusinessAccountMember ||
        !businessAccountId ||
        !businessAccountName
    ) {
        return null
    }

    return (
        <Box>
            {modalVisible && (
                <RequestBusinessAccountInviteModal
                    businessAccountId={businessAccountId}
                    businessAccountName={businessAccountName}
                    visible={modalVisible}
                    onClose={() => {
                        setModalVisible(false)
                    }}
                    onFailedInvites={setFailedEmails}
                />
            )}
            {!!failedEmails.length && (
                <FailedInvitesModal
                    failedEmails={failedEmails}
                    onClose={() => {
                        setFailedEmails([])
                    }}
                />
            )}
            <Button
                name={`${PLACEMENT}-invite-button`}
                placement={PLACEMENT}
                data-test-target={`${PLACEMENT}-invite-button`}
                onClick={() => setModalVisible(true)}
                type={showAsLink ? 'link' : 'primary'}
                style={{ lineHeight: 1.6 }}
            >
                <MailOutlined />
                Invite to {businessAccountName}
            </Button>
        </Box>
    )
}
